import React, { useEffect } from 'react';
import proptypes from 'prop-types';
import {
  arrayOf,
  customType,
  namedFragment,
  params,
  shape,
  string as stringType,
  typename,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { useImpression } from '@thd-olt-component-react/impression';
import { ThemeProvider } from '@one-thd/sui-atomic-components';
import { getThemeByName } from '@thd-olt-component-react/stencil-themes';
import { BannerRenderer } from './BannerRenderer';
import { withImpressions } from '../hocs/withImpressions';
import { normalizeSlotData } from './utils/normalizeSlotData';

const { bool, number, string } = proptypes;

const PiccolaPencil = withImpressions(
  ({
    componentClass = '',
    componentId,
    componentPosition = 1,
    enableB2CRadius = false,
    lazyLoad = false,
    theme = undefined,
    slotContentId: slotName = '',
    mediaCollectionIndex = 0,
  }) => {
    useEffect(() => {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('piccola-pencil.ready');
    }, []);

    const {
      data: componentData,
      loading: componentLoading,
      error: componentError,
    } = useDataModel('component', {
      variables: {
        componentClass,
        id: componentId,
      },
      skip: !!slotName,
    });

    const {
      data: slotData,
      loading: slotLoading,
      error: slotError,
    } = useDataModel('slot', {
      fetchPolicy: 'cache-only',
      variables: { id: slotName },
      skip: !slotName,
    });

    const normalizedData = normalizeSlotData({
      slotData,
      componentData,
      mediaCollectionIndex,
    });

    const { ref, clickID } = useImpression({
      data: {
        id: componentId || normalizedData?.entryId || 'piccola-pencil',
        name: PiccolaPencil.displayName,
        component: PiccolaPencil.displayName,
        position: componentPosition,
        type: 'content',
      },
    });

    const isLoading = componentLoading || slotLoading;
    const hasError = componentError || slotError;

    if (isLoading || hasError || !normalizedData) {
      return null;
    }

    const { desktopSourceData, mobileSourceData } = normalizedData;

    return (
      <ThemeProvider theme={getThemeByName(theme)}>
        <div
          // eslint-disable-next-line react/no-unknown-property
          clickid={clickID}
          data-component-id={componentId || normalizedData?.entryId}
          data-component-name={PiccolaPencil.displayName}
          data-component-position={componentPosition}
          data-component="PiccolaPencil"
          id={`${componentClass}-${componentPosition}-${componentId || normalizedData?.entryId}`}
          ref={ref}
        >
          <BannerRenderer
            data={normalizedData}
            desktopSourceData={desktopSourceData}
            mobileSourceData={mobileSourceData}
            mobileAspectRatio="sui-aspect-[125/29]"
            tabletAspectRatio="md:sui-aspect-[1160/60]"
            desktopAspectRatio="xl:sui-aspect-[1160/60]"
            lazyLoad={lazyLoad}
            componentId={componentId || normalizedData?.id}
            componentPosition={componentPosition}
            enableB2CRadius={enableB2CRadius}
          />
        </div>
      </ThemeProvider>
    );
  },
);

PiccolaPencil.propTypes = {
  componentClass: string,
  componentId: string,
  componentPosition: number,
  enableB2CRadius: bool,
  lazyLoad: bool,
  theme: string,
  slotContentId: string,
  mediaCollectionIndex: number,
};

PiccolaPencil.displayName = 'PiccolaPencil';

// Do not touch
const DamMediaFragment = namedFragment({
  inline: false,
  fragmentType: 'DamMedia',
  fragmentAlias: 'DamMediaV1',
}).shape({
  damContentSelector: shape({
    assetData: arrayOf(
      shape({
        selectedImageUrl: stringType(),
      }),
    ),
  }),
  damDownloadedContent: shape({
    url: stringType(),
  }),
  id: stringType(),
  imageHotspot: shape({
    id: stringType(),
    componentName: stringType(),
  }),
  __typename: typename('DamMedia'),
});

PiccolaPencil.dataModel = {
  component: params({
    id: stringType().isRequired(),
    componentClass: customType('ComponentClass').enum(['PiccolaPencil']),
  }).shape({
    PiccolaPencil: namedFragment({
      inline: true,
      fragmentType: 'PiccolaPencil',
    }).shape({
      componentName: stringType(),
      id: stringType(),
      link: stringType(),
      altText: stringType(),
      desktopImage: DamMediaFragment,
      mobileImage: DamMediaFragment,
      tabletImage: DamMediaFragment,
      theme: stringType(),
    }),
  }),
  slot: params({
    id: stringType().isRequired(),
  }).shape({
    id: stringType(),
    entryId: stringType(),
    mediaCollections: arrayOf(
      shape({
        entryId: stringType(),
        theme: stringType(),
        media: arrayOf(
          shape({
            entryId: stringType(),
            theme: stringType(),
            sourceDesktop: arrayOf(
              shape({
                entryId: stringType(),
                type: stringType(),
                uri: stringType(),
                hyperlink: stringType(),
                altText: stringType(),
                hotspot: stringType(),
              }),
            ),
            sourceMobile: arrayOf(
              shape({
                entryId: stringType(),
                type: stringType(),
                uri: stringType(),
                hyperlink: stringType(),
                altText: stringType(),
                hotspot: stringType(),
              }),
            ),
          }),
        ),
      }),
    ),
    __typename: typename('newSlot'),
  }),
};

export { PiccolaPencil };
